/**
 * 代理購入サービス設定ユーティリティ
 *
 * @file proxyServiceConfig.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * DisplaySettingsスナップショットのproxyServicesを解析し、代理購入サービス
 * （ヤフオク・メルカリ）の有効/無効を判定するユーティリティ関数を提供。
 */

import { getDisplaySettingsSnapshot } from '@/contexts/displaySettingsSnapshot';

export type ProxyService = 'yauction' | 'mercari';

/**
 * DisplaySettingsスナップショットから有効なサービス一覧を取得
 */
export const getEnabledServices = (): ProxyService[] => {
  const proxyServices = getDisplaySettingsSnapshot().proxyServices;

  const validServices: ProxyService[] = ['yauction', 'mercari'];
  const parsedServices = proxyServices.map((s: string) => s.trim().toLowerCase());

  return parsedServices.filter((s): s is ProxyService =>
    validServices.includes(s as ProxyService)
  );
};

/**
 * ヤフオクサービスが有効か判定
 */
export const isYauctionEnabled = (): boolean => {
  return getEnabledServices().includes('yauction');
};

/**
 * メルカリサービスが有効か判定
 */
export const isMercariEnabled = (): boolean => {
  return getEnabledServices().includes('mercari');
};

/**
 * いずれかの代理購入サービスが有効か判定
 * （代理購入セクション全体の表示制御用）
 */
export const isAnyProxyEnabled = (): boolean => {
  return getEnabledServices().length > 0;
};

/**
 * 両方の代理購入サービスが有効か判定
 * （フィルターボタン表示制御用）
 */
export const isBothEnabled = (): boolean => {
  const services = getEnabledServices();
  return services.includes('yauction') && services.includes('mercari');
};

/**
 * サービスが有効かどうかを判定する汎用関数
 */
export const isServiceEnabled = (service: ProxyService): boolean => {
  return getEnabledServices().includes(service);
};
